import {
  describeProviderOutputLimit,
  extractOpenRouterErrorMessage,
  stripOpenRouterUrls,
} from "@/lib/openRouterErrors";
import { getChatStreamTimeoutMs } from "@/lib/chatStreamTiming";
import { AIProxyHttpError } from "@/services/aiProxyClient";

export type ChatErrorKind =
  | "timeout"
  | "aborted"
  | "network"
  | "api"
  | "empty_response"
  | "provider"
  | "loop"
  | "unknown";

export type ParsedChatError = {
  kind: ChatErrorKind;
  title: string;
  message: string;
  httpStatus?: number;
};

const SETTINGS_HINT =
  "Increase the stream timeout in Chat Settings, or tap Continue below to pick up from the partial reply.";

function extractHttpStatus(text: string): number | undefined {
  const match = text.match(/API Error:\s*(\d{3})/i) ?? text.match(/\b(status|code)[:\s]+(\d{3})\b/i);
  if (!match) return undefined;
  const status = Number(match[match.length - 1]);
  return Number.isFinite(status) ? status : undefined;
}

function isAbortLike(errorLike: unknown): boolean {
  if (errorLike instanceof DOMException && errorLike.name === "AbortError") return true;
  if (errorLike instanceof Error && errorLike.name === "AbortError") return true;
  const msg = typeof errorLike === "string" ? errorLike : errorLike instanceof Error ? errorLike.message : "";
  return /BodyStreamBuffer was aborted|aborted|abort/i.test(msg);
}

function isTimeoutLike(errorLike: unknown): boolean {
  if (errorLike instanceof DOMException && errorLike.name === "TimeoutError") return true;
  const msg = typeof errorLike === "string" ? errorLike : errorLike instanceof Error ? errorLike.message : "";
  return /timeout|maximum duration|exceeded maximum/i.test(msg);
}

function isNetworkLike(msg: string): boolean {
  return (
    /NetworkError/i.test(msg) ||
    /Failed to fetch/i.test(msg) ||
    /network error/i.test(msg) ||
    /load failed/i.test(msg)
  );
}

function isEmptyResponse(msg: string): boolean {
  return /empty response|no response received|returned an empty/i.test(msg);
}

function isLoopLike(msg: string): boolean {
  return /stuck in a loop|repetitive output/i.test(msg);
}

const GENERIC_UPSTREAM_MESSAGES = new Set([
  "provider returned error",
  "openrouter upstream failure",
]);

function isOpenRouterBillingMessage(msg: string): boolean {
  return /requires more credits|insufficient credits|payment required/i.test(msg);
}

function isSpecificUpstreamMessage(msg: string): boolean {
  const trimmed = msg.trim();
  if (!trimmed) return false;
  if (GENERIC_UPSTREAM_MESSAGES.has(trimmed.toLowerCase())) return false;
  return trimmed.length > 0;
}

function extractRawMessage(errorLike: unknown): string | null {
  if (errorLike instanceof AIProxyHttpError) {
    return (
      extractOpenRouterErrorMessage(errorLike.bodyJson) ??
      extractOpenRouterErrorMessage(errorLike.bodyText) ??
      (errorLike.message.trim() || null)
    );
  }
  return extractOpenRouterErrorMessage(errorLike);
}

function httpStatusMessage(status: number): string {
  switch (status) {
    case 401:
      return "Authentication failed. Check your API key configuration.";
    case 402:
      return "The AI provider account is out of credits. Contact support if this persists.";
    case 403:
      return "Access denied. This model or feature may not be available on your plan.";
    case 404:
      return "The AI service endpoint was not found. Try again or contact support if this persists.";
    case 429:
      return "Rate limit reached. Wait a moment and try again.";
    case 502:
    case 503:
    case 504:
      return "The AI provider is temporarily unavailable. Try again in a few minutes.";
    default:
      return `The AI service returned an error (${status}). Please try again.`;
  }
}

export function parseChatError(
  errorLike: unknown,
  options?: { timedOut?: boolean; httpStatus?: number }
): ParsedChatError {
  const fromOpenRouter = extractRawMessage(errorLike);
  const raw =
    fromOpenRouter ??
    (typeof errorLike === "string" && errorLike.trim()
      ? errorLike.trim()
      : errorLike instanceof Error && errorLike.message.trim()
        ? errorLike.message.trim()
        : "Failed to get AI response.");

  if (
    errorLike instanceof TypeError ||
    /is not a function/i.test(raw) ||
    /\.startsWith/i.test(raw)
  ) {
    return {
      kind: "unknown",
      title: "Response failed",
      message: "Something went wrong while processing the response. Please try again.",
    };
  }

  if (isAbortLike(errorLike) && !isTimeoutLike(errorLike) && !options?.timedOut) {
    return {
      kind: "aborted",
      title: "Chat interrupted",
      message: `The response stream was stopped. ${SETTINGS_HINT}`,
    };
  }

  if (options?.timedOut || isTimeoutLike(errorLike) || isTimeoutLike(raw)) {
    const seconds = Math.round(getChatStreamTimeoutMs() / 1000);
    const limitLabel = seconds > 0 ? `${seconds}s` : "the configured limit";
    return {
      kind: "timeout",
      title: "Response timed out",
      message: `The model is still generating but hit the stream timeout (${limitLabel}). ${SETTINGS_HINT}`,
    };
  }

  if (isLoopLike(raw)) {
    return {
      kind: "loop",
      title: "Repetitive output detected",
      message: "The model appears stuck in a loop. Try Retry or switch to a different model.",
    };
  }

  if (isEmptyResponse(raw)) {
    return {
      kind: "empty_response",
      title: "Empty response",
      message: "The model returned no content. Try Retry or choose a different model.",
    };
  }

  if (raw.includes("API key") || /not configured/i.test(raw)) {
    return {
      kind: "api",
      title: "AI not configured",
      message: "AI service not configured. Please check your API key.",
    };
  }

  if (isNetworkLike(raw)) {
    return {
      kind: "network",
      title: "Network error",
      message: "Could not reach the AI service. Check your connection and try again.",
    };
  }

  const httpStatus = options?.httpStatus ?? extractHttpStatus(raw);

  const outputLimit = describeProviderOutputLimit(raw);
  if (outputLimit) {
    return {
      kind: "api",
      title: outputLimit.title,
      message: outputLimit.message,
      httpStatus: httpStatus ?? 402,
    };
  }

  if (httpStatus === 402 || isOpenRouterBillingMessage(raw)) {
    return {
      kind: "api",
      title: "AI provider credits depleted",
      message: isSpecificUpstreamMessage(raw)
        ? stripOpenRouterUrls(raw.replace(/VITE_CHAT_STREAM_TIMEOUT_MS/gi, "Chat Settings → Stream timeout"))
        : httpStatusMessage(402),
      httpStatus: httpStatus ?? 402,
    };
  }

  if (httpStatus && isSpecificUpstreamMessage(raw)) {
    return {
      kind: "provider",
      title: "Provider error",
      message: stripOpenRouterUrls(raw.replace(/VITE_CHAT_STREAM_TIMEOUT_MS/gi, "Chat Settings → Stream timeout")),
      httpStatus,
    };
  }

  if (httpStatus) {
    return {
      kind: "api",
      title: "AI service error",
      message: httpStatusMessage(httpStatus),
      httpStatus,
    };
  }

  if (/provider returned error|provider not found|upstream failure/i.test(raw)) {
    return {
      kind: "provider",
      title: "Provider error",
      message: stripOpenRouterUrls(raw.replace(/VITE_CHAT_STREAM_TIMEOUT_MS/gi, "Chat Settings → Stream timeout")),
    };
  }

  if (/Streaming failed|Failed to parse/i.test(raw)) {
    return {
      kind: "unknown",
      title: "Streaming failed",
      message: stripOpenRouterUrls(raw.replace(/VITE_CHAT_STREAM_TIMEOUT_MS/gi, "Chat Settings → Stream timeout")),
    };
  }

  return {
    kind: "unknown",
    title: "Chat error",
    message: stripOpenRouterUrls(raw.replace(/VITE_CHAT_STREAM_TIMEOUT_MS/gi, "Chat Settings → Stream timeout")),
  };
}

/** @deprecated Use parseChatError(...).message */
export function buildUserFacingChatErrorMessage(errorLike: unknown, options?: { timedOut?: boolean }): string {
  return parseChatError(errorLike, options).message;
}

export function getChatErrorTitle(errorLike: unknown, options?: { timedOut?: boolean }): string {
  return parseChatError(errorLike, options).title;
}
